import React, { useContext, createContext, useState, MouseEventHandler, useEffect } from 'react';
import config from './Config';
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { getPhoto, getUser } from './GraphService';
import { useTranslation } from 'react-i18next';

export interface UserAvatarProps {
  user: AppUser,
  classname?: string
};

export interface AppUser {
  displayName?: string,
  jobTitle?: string,
  email?: string,
  avatar?: string,
  timeZone?: string,
  timeFormat?: string
};

export interface AppError {
  message: string,
  debug?: string
};

type AppContext = {
  user?: AppUser;
  error?: AppError;
  signIn?: MouseEventHandler<HTMLElement>;
  signOut?: MouseEventHandler<HTMLElement>;
  displayError?: Function;
  clearError?: Function;
  authProvider?: AuthCodeMSALBrowserAuthenticationProvider;
}

const appContext = createContext<AppContext>({
  user: undefined,
  error: undefined,
  signIn: undefined,
  signOut: undefined,
  displayError: undefined,
  clearError: undefined,
  authProvider: undefined
});

export function useAppContext(): AppContext {
  return useContext(appContext);
}

interface ProvideAppContextProps {
  children: React.ReactNode;
}

export default function ProvideAppContext({ children }: ProvideAppContextProps) {
  const auth = useProvideAppContext();
  return (
    <appContext.Provider value={auth}>
      {children}
    </appContext.Provider>
  );
}

function useProvideAppContext() {
  const [user, setUser] = useState<AppUser | undefined>(undefined);
  const [error, setError] = useState<AppError | undefined>(undefined);

  const msal = useMsal();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const displayError = (message: string, debug?: string) => {
    setError({ message, debug });
  }

  const clearError = () => {
    setError(undefined);
  }

  const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
    msal.instance as PublicClientApplication,
    {
      account: msal.instance.getActiveAccount()!,
      scopes: config.scopes,
      interactionType: InteractionType.Redirect
    }
  );

  useEffect(() => {
    const checkUser = async () => {
      if (!user) {
        try {
          const account = msal.instance.getActiveAccount();
          if (account) {
            const user = await getUser(authProvider);
            const photo = await getPhoto(authProvider).catch(() => '');

            const extensionAttributes = user.onPremisesExtensionAttributes;
            const localizedDisplayName = currentLanguage === 'ar' && extensionAttributes ? extensionAttributes.extensionAttribute1 : user.displayName;
            const localizedJobTitle = currentLanguage === 'ar' && extensionAttributes ? extensionAttributes.extensionAttribute2 : user.jobTitle;

            setUser({
              displayName: localizedDisplayName || user.displayName || '',
              jobTitle: localizedJobTitle || user.jobTitle || '',
              email: user.mail || user.userPrincipalName || '',
              timeFormat: user.mailboxSettings?.timeFormat || 'h:mm a',
              timeZone: user.mailboxSettings?.timeZone || 'UTC',
              avatar: photo
            });
          }
        } catch (err: any) {
          displayError(err.message);
        }
      }
    };
    checkUser();
  });

  const signIn = async () => {
    await msal.instance.loginRedirect({
      scopes: config.scopes,
      prompt: 'select_account'
    });

    const user = await getUser(authProvider);
    const photo = await getPhoto(authProvider).catch(() => '');

    const extensionAttributes = user.onPremisesExtensionAttributes;
    const localizedDisplayName = currentLanguage === 'ar' && extensionAttributes ? extensionAttributes.extensionAttribute1 : user.displayName;
    const localizedJobTitle = currentLanguage === 'ar' && extensionAttributes ? extensionAttributes.extensionAttribute2 : user.jobTitle;

    setUser({
      displayName: localizedDisplayName || user.displayName || '',
      jobTitle: localizedJobTitle || user.jobTitle || '',
      email: user.mail || user.userPrincipalName || '',
      timeFormat: user.mailboxSettings?.timeFormat || '',
      timeZone: user.mailboxSettings?.timeZone || 'UTC',
      avatar: photo
    });
  };

  const signOut = async () => {
    await msal.instance.logoutPopup();
    setUser(undefined);
  };

  return {
    user,
    error,
    signIn,
    signOut,
    displayError,
    clearError,
    authProvider
  };
}
